import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import signatureLogoUrl from '../../config/signature-logo-url.js';

const Signature = ({ name, title, email, phone, ext }) => {
    const signatureRef = useRef(null);
    const [error, setError] = useState(null);
    const [copySuccess, setCopySuccess] = useState('');
    const DISCLAIMER_TEXT =
        'This email, including any attachments, is confidential, may be legally privileged, and is intended solely for the named recipient(s). If you received this email in error, please notify the sender immediately, destroy any hard copy, and permanently delete this email and any attachments from your system. If you are not the intended recipient, your access to this email is unauthorized, and any disclosure, copying, distribution, or use of the contents of this email, including any attachments, for any reason is strictly prohibited. Emails may contain viruses, worms or other malicious code, and the recipient accepts full responsibility for taking protective action against all of the foregoing. eAssist is not liable for any damage or loss arising from this email, including any attachments. If you have received this email in error, or would like to opt out of future marketing emails, please reply immediately to the sender. Thank you. 1220 S 630 E STE 450, American Fork, UT 84003';

    const getPhoneLine = () => {
        if (ext && phone) {
            return `+${ext} ${phone}`;
        }

        if (ext) {
            return `+${ext}`;
        }

        return phone;
    };

    const copySignature = async () => {
        if (!signatureRef.current) {
            setError(new Error('Signature block could not be found.'));
            setCopySuccess('');
            return;
        }

        setError(null);
        setCopySuccess('');

        const html = signatureRef.current.innerHTML;
        const text = signatureRef.current.innerText;

        try {
            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' })
                    })
                ]);
                setCopySuccess(
                    'Signature copied. You can paste it into Gmail settings.'
                );
                return;
            }

            const range = document.createRange();
            range.selectNodeContents(signatureRef.current);

            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);

            const copied = document.execCommand('copy');
            selection.removeAllRanges();

            if (!copied) {
                throw new Error('Copy command was not successful.');
            }

            setCopySuccess(
                'Signature copied. You can paste it into Gmail settings.'
            );
        } catch (err) {
            setError(
                new Error(
                    'Copy failed in this browser. Highlight the signature and copy manually.'
                )
            );
        }
    };

    return (
        <>
            <div
                style={{
                    marginTop: '4em',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '80%',
                    marginBottom: '5em'
                }}>
                <button
                    className="btn btn-primary btn-block"
                    onClick={copySignature}
                    style={{ marginBottom: '1.5em' }}>
                    Copy Signature
                </button>
                <div
                    ref={signatureRef}
                    style={{
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '14px',
                        width: '100%',
                        maxWidth: '800px'
                    }}>
                    <p style={{ marginBottom: 0, marginTop: 0 }}>
                        <b>{name}</b>
                    </p>
                    <p style={{ marginBottom: 0, marginTop: 0 }}>
                        <em>{title}</em>
                    </p>
                    <p style={{ marginBottom: 0, marginTop: 0 }}>{email}</p>
                    {getPhoneLine() && (
                        <p style={{ marginBottom: 0, marginTop: 0 }}>
                            {getPhoneLine()}
                        </p>
                    )}

                    <div style={{ height: '10px' }} />

                    <div
                        style={{
                            width: '180px',
                            height: '60px',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                        <img
                            src={signatureLogoUrl}
                            alt="eAssist Dental Solutions logo"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </div>

                    <div style={{ height: '10px' }} />

                    <p
                        style={{
                            fontSize: '10px',
                            width: '100%',
                            maxWidth: '800px',
                            marginBottom: 0,
                            marginTop: 0
                        }}>
                        <em>{DISCLAIMER_TEXT}</em>
                    </p>
                </div>
                {copySuccess && <SuccessMessage message={copySuccess} />}
                {error && <ErrorMessage message={error.message} />}
            </div>
        </>
    );
};

Signature.propTypes = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string,
    ext: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Signature.defaultProps = {
    phone: '',
    ext: ''
};

export default Signature;
